(() => {
  const instances = new WeakMap();
  function initialize(root) {
    root.querySelectorAll('[data-ratings]').forEach(section => {
      if (instances.has(section)) return;
      const controller = new AbortController(), options = { signal: controller.signal };
      const viewport = section.querySelector('[data-rating-viewport]');
      const originals = [...viewport.querySelectorAll('[data-rating-card]')];
      const dots = [...section.querySelectorAll('[data-rating-index]')];
      const count = originals.length, reduce = matchMedia('(prefers-reduced-motion: reduce)');
      const boundaryCount = Math.min(3, count);
      const before = originals.slice(-boundaryCount).map(card => card.cloneNode(true));
      const after = originals.slice(0, boundaryCount).map(card => card.cloneNode(true));
      [...before, ...after].forEach(card => { card.setAttribute('aria-hidden', 'true'); card.dataset.ratingClone = ''; });
      viewport.prepend(...before); viewport.append(...after);
      const cards = [...viewport.children];
      let current = 0, physical = boundaryCount, timer, settleTimer, frame, visible = false, hovered = false, dragging = false, interacting = false;
      let startX = 0, startScroll = 0, moved = false, announce = false, resetFrame;
      const offset = index => cards[index].offsetLeft - cards[0].offsetLeft;
      const stride = () => offset(cards.length - 1) / (cards.length - 1);
      function schedule() {
        clearTimeout(timer);
        if (reduce.matches || !visible || hovered || dragging || interacting || document.hidden || section.contains(document.activeElement)) return;
        timer = setTimeout(() => go(current + 1, false), 4200);
      }
      function paint() {
        current = (physical - boundaryCount + count) % count;
        cards.forEach((card, index) => card.classList.toggle('is-active', index === physical));
        dots.forEach((dot, index) => dot.setAttribute('aria-current', String(index === current)));
      }
      function position(index) {
        section.classList.add('is-resetting');
        physical = index;
        paint();
        viewport.scrollTo({ left: offset(index), behavior: 'instant' });
        cancelAnimationFrame(resetFrame);
        resetFrame = requestAnimationFrame(() => { resetFrame = requestAnimationFrame(() => section.classList.remove('is-resetting')); });
      }
      function settle() {
        if (dragging || Math.abs(viewport.scrollLeft - offset(physical)) > 2) return;
        if (physical < boundaryCount) position(physical + count);
        else if (physical >= boundaryCount + count) position(physical - count);
        if (announce) {
          section.querySelector('[data-rating-status]').textContent = originals[current].getAttribute('aria-label');
          announce = false;
        }
        schedule();
      }
      function go(index, manual = true) {
        announce = manual;
        const target = Math.max(0, Math.min(cards.length - 1, index + boundaryCount));
        viewport.scrollTo({ left: offset(target), behavior: reduce.matches ? 'instant' : 'smooth' });
        schedule();
      }
      viewport.addEventListener('scroll', () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          physical = Math.max(0, Math.min(cards.length - 1, Math.round(viewport.scrollLeft / stride())));
          paint();
          clearTimeout(settleTimer); settleTimer = setTimeout(settle, 160);
          schedule();
        });
      }, { ...options, passive: true });
      viewport.addEventListener('scrollend', settle, options);
      section.querySelector('[data-rating-prev]').addEventListener('click', () => go(current - 1), options);
      section.querySelector('[data-rating-next]').addEventListener('click', () => go(current + 1), options);
      dots.forEach(dot => dot.addEventListener('click', () => go(Number(dot.dataset.ratingIndex)), options));
      viewport.addEventListener('keydown', event => {
        const keys = { ArrowLeft: current - 1, ArrowRight: current + 1, Home: 0, End: count - 1 };
        if (!(event.key in keys)) return;
        event.preventDefault(); go(keys[event.key]);
      }, options);
      viewport.addEventListener('pointerdown', event => {
        if (event.pointerType !== 'mouse') { interacting = true; schedule(); return; }
        if (event.button !== 0) return;
        dragging = true; moved = false; startX = event.clientX; startScroll = viewport.scrollLeft; schedule();
      }, options);
      viewport.addEventListener('pointermove', event => {
        if (!dragging) return;
        const delta = event.clientX - startX;
        if (!moved && Math.abs(delta) < 6) return;
        moved = true; viewport.classList.add('is-dragging');
        viewport.setPointerCapture(event.pointerId);
        viewport.scrollLeft = startScroll - delta;
      }, options);
      function endDrag() {
        interacting = false; schedule();
        if (!dragging) return;
        dragging = false; viewport.classList.remove('is-dragging');
        if (moved) {
          physical = Math.max(0, Math.min(cards.length - 1, Math.round(viewport.scrollLeft / stride())));
          viewport.scrollTo({ left: offset(physical), behavior: reduce.matches ? 'instant' : 'smooth' });
          announce = true;
        }
        schedule();
      }
      window.addEventListener('pointerup', endDrag, options);
      window.addEventListener('pointercancel', endDrag, options);
      viewport.addEventListener('pointerenter', event => { if (event.pointerType === 'mouse') { hovered = true; schedule(); } }, options);
      viewport.addEventListener('pointerleave', () => { hovered = false; schedule(); }, options);
      section.addEventListener('focusin', schedule, options);
      section.addEventListener('focusout', () => queueMicrotask(schedule), options);
      document.addEventListener('visibilitychange', schedule, options);
      reduce.addEventListener('change', () => { if (reduce.matches) position(current + boundaryCount); schedule(); }, options);
      const observer = new IntersectionObserver(entries => { visible = entries[0].isIntersecting && entries[0].intersectionRatio >= .25; schedule(); }, { threshold: .25 });
      observer.observe(viewport);
      const resize = new ResizeObserver(() => position(current + boundaryCount));
      resize.observe(viewport);
      section.dataset.ratingsReady = 'true';
      position(boundaryCount);
      section.querySelector('[data-rating-controls]').hidden = false;
      instances.set(section, () => {
        controller.abort(); observer.disconnect(); resize.disconnect();
        [timer, settleTimer].forEach(clearTimeout); [frame, resetFrame].forEach(cancelAnimationFrame);
        [...before, ...after].forEach(card => card.remove()); delete section.dataset.ratingsReady;
      });
    });
  }
  initialize(document);
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('[data-ratings]').forEach(section => { instances.get(section)?.(); instances.delete(section); }));
})();
